import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import TurnstileWidget from "../components/TurnstileWidget";
import { submitContact } from "../utils/contact";
import { verifyTurnstileToken } from "../utils/turnstile";

type AppointmentFormValues = {
  name: string;
  email: string;
  phone: string;
  date: string;
  time: string;
  message: string;
  turnstileToken: string;
};

type AppointmentCard = {
  title: string;
  body: string;
};

const CARD_ICONS = [CalendarMonthRoundedIcon, AutoAwesomeRoundedIcon, PhoneInTalkRoundedIcon];

export default function TerminVereinbaren() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [captchaKey, setCaptchaKey] = useState(0);
  const contactEmail = "info@rss-energie.de";
  const copyEmail = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(contactEmail);
        toast.success(t("common.footer.copyEmailSuccess"));
        return;
      }
      const textarea = document.createElement("textarea");
      textarea.value = contactEmail;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (ok) {
        toast.success(t("common.footer.copyEmailSuccess"));
      } else {
        toast.error(t("common.footer.copyError"));
      }
    } catch {
      toast.error(t("common.footer.copyError"));
    }
  };

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";
  const requiresCaptcha = Boolean(siteKey);

  const appointmentSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, t("appointment.form.validation.name")),
        email: z
          .string()
          .min(1, t("appointment.form.validation.email"))
          .email(t("appointment.form.validation.emailInvalid")),
        phone: z.string().min(1, t("appointment.form.validation.phone")),
        date: z.string().min(1, t("appointment.form.validation.date")),
        time: z.string().min(1, t("appointment.form.validation.time")),
        message: z
          .string()
          .min(1, t("appointment.form.validation.message"))
          .min(20, t("appointment.form.validation.messageMin")),
        turnstileToken: requiresCaptcha
          ? z.string().min(1, t("common.captcha.validation"))
          : z.string().optional(),
      }),
    [t, requiresCaptcha]
  );

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      date: "",
      time: "",
      message: "",
      turnstileToken: "",
    },
  });

  const cards = useMemo(() => {
    const value = t("appointment.cards", { returnObjects: true });
    return (Array.isArray(value) ? (value as AppointmentCard[]) : []).slice(0, 3);
  }, [t]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 999,
      background: isDark ? "rgba(2, 22, 18, 0.45)" : "rgba(255, 255, 255, 0.72)",
      "& fieldset": {
        borderColor: isDark ? "rgba(172, 210, 194, 0.28)" : "rgba(29, 43, 42, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: isDark ? "rgba(191, 224, 211, 0.4)" : "rgba(29, 43, 42, 0.35)",
      },
      "&.Mui-focused fieldset": {
        borderColor: isDark ? "rgba(141, 224, 198, 0.75)" : "rgba(47, 111, 95, 0.65)",
      },
    },
    "& .MuiInputBase-input": {
      color: isDark ? "#d9e8e2" : "inherit",
    },
    "& .MuiInputLabel-root": {
      color: isDark ? "rgba(217, 232, 226, 0.72)" : "text.secondary",
    },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      background: isDark ? "rgba(7, 33, 28, 0.95)" : "var(--surface)",
      paddingInline: 6,
      borderRadius: 6,
    },
  } as const;

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        background: isDark
          ? "radial-gradient(circle at 12% 0%, rgba(66, 145, 124, 0.26) 0%, rgba(6, 24, 21, 0) 50%)"
          : "none",
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: { xs: 4, md: 5 },
            border: isDark ? "1px solid rgba(175, 220, 202, 0.22)" : "1px solid var(--border)",
            background: isDark
              ? "linear-gradient(112deg, rgba(40, 104, 90, 0.92) 0%, rgba(60, 155, 131, 0.9) 68%, rgba(170, 191, 113, 0.86) 100%)"
              : "linear-gradient(112deg, #2c7c69 0%, #4ea893 68%, #a7bf72 100%)",
            color: "#f1f6f3",
            mb: { xs: 3, md: 5 },
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              right: -90,
              top: -90,
              width: 320,
              height: 320,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255, 221, 126, 0.38) 0%, rgba(255, 221, 126, 0) 70%)",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography variant="h1">{t("appointment.hero.title")}</Typography>
            <Typography variant="body1" sx={{ mt: 1.5, maxWidth: 760, color: "rgba(241,246,243,0.9)" }}>
              {t("appointment.hero.subtitle")}
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: isDark ? "1px solid rgba(173, 217, 201, 0.22)" : "1px solid var(--border)",
                background: isDark
                  ? "linear-gradient(165deg, rgba(6, 30, 25, 0.9) 0%, rgba(7, 35, 29, 0.82) 100%)"
                  : "var(--surface)",
              }}
            >
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      color: isDark ? "#dfeae5" : "text.primary",
                      lineHeight: 1.1,
                    }}
                  >
                    {t("appointment.info.title")}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, color: isDark ? "rgba(210, 225, 218, 0.76)" : "text.secondary" }}>
                    {t("appointment.info.body")}
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  {cards.map((card, index) => {
                    const Icon = CARD_ICONS[index] ?? CalendarMonthRoundedIcon;
                    return (
                      <Box
                        key={card.title}
                        sx={{
                          p: 2.4,
                          borderRadius: 999,
                          border: isDark ? "1px solid rgba(177, 215, 201, 0.2)" : "1px solid var(--border)",
                          background: isDark
                            ? "linear-gradient(120deg, rgba(100, 131, 166, 0.34) 0%, rgba(58, 98, 88, 0.26) 55%, rgba(37, 84, 63, 0.28) 100%)"
                            : "linear-gradient(120deg, rgba(217,228,237,0.72) 0%, rgba(207,227,219,0.68) 100%)",
                        }}
                      >
                        <Stack direction="row" spacing={1.6} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              border: isDark ? "1px solid rgba(173, 212, 248, 0.42)" : "1px solid rgba(29,43,42,0.22)",
                              color: isDark ? "#8fd2ff" : "#2f6f5f",
                              background: isDark ? "rgba(41, 75, 98, 0.45)" : "rgba(255,255,255,0.5)",
                              flexShrink: 0,
                            }}
                          >
                            <Icon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ color: isDark ? "#dce8e3" : "text.primary", fontWeight: 700 }}
                            >
                              {card.title}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.3, color: isDark ? "rgba(206, 221, 214, 0.76)" : "text.secondary" }}>
                              {card.body}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>

                <Box sx={{ mt: 3, pt: 3, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)", display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                  <img
                    src="/assets/van_no_bg.png"
                    alt="RSS-Energie Firmenwagen"
                    style={{ width: "90%", maxWidth: "400px", height: "auto", display: "block", filter: isDark ? "drop-shadow(0 10px 15px rgba(0,0,0,0.5))" : "drop-shadow(0 10px 15px rgba(0,0,0,0.15))" }}
                  />
                  <img
                    src={isDark ? "/logo_transparent_darkmode.png" : "/logo_transparent.png"}
                    alt="RSS-Energie Logo"
                    style={{ width: "120px", height: "auto", position: "absolute", bottom: -10, right: 10, mixBlendMode: isDark ? "screen" : "multiply", opacity: 0.8 }}
                  />
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: isDark ? "1px solid rgba(173, 217, 201, 0.22)" : "1px solid var(--border)",
                background: isDark
                  ? "linear-gradient(165deg, rgba(6, 30, 25, 0.9) 0%, rgba(7, 35, 29, 0.82) 100%)"
                  : "var(--surface)",
              }}
            >
              <Stack
                spacing={2}
                component="form"
                onSubmit={handleSubmit(async (values) => {
                  if (requiresCaptcha) {
                    const ok = await verifyTurnstileToken(values.turnstileToken);
                    if (!ok) {
                      toast.error(t("common.captcha.error"));
                      setValue("turnstileToken", "");
                      setCaptchaKey((value) => value + 1);
                      return;
                    }
                  }

                  const message = [
                    `Name: ${values.name}`,
                    `E-Mail: ${values.email}`,
                    `Telefon: ${values.phone}`,
                    `Wunschtermin: ${values.date}`,
                    `Uhrzeit: ${values.time}`,
                    "",
                    values.message,
                  ].join("\n");

                  toast.loading(t("appointment.toast.sending"), { id: "appointment" });
                  const result = await submitContact({
                    name: values.name,
                    email: values.email,
                    subject: "Terminanfrage",
                    message,
                  });

                  if (!result.ok) {
                    const errorKey = result.status === 429 ? "common.toast.rateLimited" : "common.toast.error";
                    toast.error(t(errorKey), { id: "appointment" });
                    return;
                  }

                  toast.success(t("appointment.toast.success"), { id: "appointment" });
                  reset();
                  setCaptchaKey((value) => value + 1);
                  navigate("/nachricht-gesendet?type=termin");
                })}
              >
                <Typography variant="h3" sx={{ color: isDark ? "#dce9e3" : "text.primary", mb: 0.5 }}>
                  {t("appointment.formTitle")}
                </Typography>

                <TextField
                  label={t("appointment.form.name")}
                  size="small"
                  fullWidth
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  {...register("name")}
                  sx={fieldSx}
                />

                <TextField
                  label={t("appointment.form.email")}
                  size="small"
                  fullWidth
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  {...register("email")}
                  sx={fieldSx}
                />

                <TextField
                  label={t("appointment.form.phone")}
                  size="small"
                  fullWidth
                  error={Boolean(errors.phone)}
                  helperText={errors.phone?.message}
                  {...register("phone")}
                  sx={fieldSx}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label={t("appointment.form.date")}
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(errors.date)}
                    helperText={errors.date?.message}
                    {...register("date")}
                    sx={{
                      flex: 1,
                      ...fieldSx,
                      "& input::-webkit-calendar-picker-indicator": {
                        filter: isDark ? "invert(1) opacity(0.82)" : "none",
                        cursor: "pointer",
                      },
                    }}
                  />
                  <TextField
                    label={t("appointment.form.time")}
                    type="time"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(errors.time)}
                    helperText={errors.time?.message}
                    {...register("time")}
                    sx={{
                      flex: 1,
                      ...fieldSx,
                      "& input::-webkit-calendar-picker-indicator": {
                        filter: isDark ? "invert(1) opacity(0.82)" : "none",
                        cursor: "pointer",
                      },
                    }}
                  />
                </Stack>

                <TextField
                  label={t("appointment.form.message")}
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(errors.message)}
                  helperText={errors.message?.message}
                  {...register("message")}
                  sx={{
                    ...fieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...fieldSx["& .MuiOutlinedInput-root"],
                      borderRadius: 3,
                      alignItems: "flex-start",
                    },
                    "& .MuiInputBase-inputMultiline": {
                      paddingTop: 12,
                      color: isDark ? "#d9e8e2" : "inherit",
                    },
                  }}
                />

                {requiresCaptcha ? (
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: isDark ? "#d5e3de" : "text.primary" }}>
                      {t("common.captcha.label")}
                    </Typography>
                    <Controller
                      control={control}
                      name="turnstileToken"
                      render={({ field }) => (
                        <TurnstileWidget
                          siteKey={siteKey}
                          resetKey={captchaKey}
                          onToken={(token) => field.onChange(token)}
                        />
                      )}
                    />
                    <Typography
                      variant="caption"
                      color={errors.turnstileToken ? "error" : "text.secondary"}
                    >
                      {errors.turnstileToken?.message ?? t("common.captcha.helper")}
                    </Typography>
                  </Stack>
                ) : null}

                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{
                    mt: 0.5,
                    borderRadius: 999,
                    py: 1.25,
                    fontSize: "1.05rem",
                    fontWeight: 700,
                  }}
                >
                  {isSubmitting ? t("appointment.form.submitLoading") : t("appointment.form.submit")}
                </Button>

                <Box
                  component="button"
                  type="button"
                  onClick={copyEmail}
                  aria-label={t("common.footer.copyEmailAria")}
                  sx={{
                    border: "none",
                    background: "none",
                    p: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    color: isDark ? "rgba(205, 220, 213, 0.72)" : "text.secondary",
                    "&:hover": { color: isDark ? "#d5e3de" : "text.primary" },
                  }}
                >
                  <Typography variant="caption">{t("appointment.emailLabel")}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
